import { inject, Injectable } from '@angular/core';
import { GlobalWebsocketService } from '@approot/global-websocket';
import { Entity } from '@approot/models';
import {
  AlertsService,
  ConfigService,
  TradesCaptureService,
  UserStorageService
} from '@approot/services';
import { clone, sleep } from '@approot/utilities';
import { isEqual } from 'lodash';
import { Throttle } from 'lodash-decorators';
import { Observable, ReplaySubject, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { mapHeaders } from './lifecycle-blotter.funcs';
import {
  LifecycleBlotterPayload as BlotterPayload,
  LifecycleBlotterState as BlotterState,
  Row,
  RunningState,
  WSRow
} from './lifecycle-blotter.types';

// Note that "entities" and "headers" are not included here as they won't change once set.
const DefaultBlotterState: BlotterState = {
  uuid: null,
  rows: [],
  nItems: null,
  runningState: RunningState.Idle,
  info: null
};

const WS_ACTION = 'apiv2_lifeCycling';
const NAME = 'Lifecycle';
const HEADER_NAME = 'defaultLifeCyclingBlotterHeaders';

@Injectable({
  providedIn: 'root'
})
export class LifecycleBlotterService {
  public stateChanged: Observable<BlotterState>;
  private state: BlotterState = {};
  // this service is permanent but the component is recreated on each tab navigation
  // so we need to use a ReplaySubject to keep the state
  private state$ = new ReplaySubject<BlotterState>(1);
  private wsSub: Subscription = null;
  private timer: any = null;
  private websocketService = inject(GlobalWebsocketService);
  private tradesCaptureService = inject(TradesCaptureService);
  private alertsService = inject(AlertsService);
  private configService = inject(ConfigService);
  private userStorageService = inject(UserStorageService);

  constructor() {
    this.setState({
      ...DefaultBlotterState,
      entities: [],
      headers: []
    });
    this.stateChanged = this.state$.asObservable();
    this.loadEntities();
    this.loadBlotterHeaders();
  }

  public update(state: BlotterState): void {
    this.setState(state);
  }

  public async runReport(payload: BlotterPayload) {
    const uuid = await this.websocketService.sendMessage({
      action: WS_ACTION,
      payload
    });
    console.log(`Running ${NAME} Report for`, payload, uuid);
    this.setState(
      { ...DefaultBlotterState, uuid, selectedEntry: payload },
      false
    );
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.timer = setTimeout(() => {
      if (this.state.runningState === RunningState.Running) {
        this.cancelReport('Report timed out');
        console.log(`${NAME} Report timed out`);
        this.alertsService.addAlert({
          type: 'error',
          message: `${NAME} Report timed out`
        });
        this.ping();
      }
    }, 5 * 60_000);
  }

  public cancelReport(info: string = null) {
    this.unsubscribe();
    this.setState({ ...DefaultBlotterState, info });
    clearTimeout(this.timer);
    this.timer = null;
  }

  /****<PRIVATE>**************************************************************/

  private entitiesFn() {
    return this.tradesCaptureService.getLifeCycledEntities();
  }

  private loadEntities(): void {
    this.entitiesFn()
      .pipe(take(1))
      .subscribe({
        next: (entities: Entity[]) => {
          const e = entities
            .map(e => e.identifier)
            .sort((a, b) =>
              a.localeCompare(b, 'en', {
                sensitivity: 'accent'
              })
            );
          this.setState({ entities: e });
        }
      });
  }

  private loadBlotterHeaders(): void {
    this.configService
      .getAgGridConfig()
      .pipe(
        take(1),
        map(config => {
          const setting = config.find(c => c.identifier === HEADER_NAME);
          const headers = config.find(c => c.identifier === 'headers');
          return mapHeaders(setting, headers);
        })
      )
      .subscribe({
        next: headers => this.setState({ headers })
      });
  }

  private setState(state: BlotterState, emit = true) {
    const ouuid = this.state.uuid;
    const newState = { ...this.state, ...state };
    if (isEqual(newState, this.state)) {
      return;
    }

    this.state = clone(newState, true);
    if (emit) {
      this.state$.next(this.state);
    }
    if (state.uuid && state.uuid !== ouuid) {
      this.subscribeToBlotter(state.uuid);
    }
  }

  private unsubscribe() {
    this.wsSub?.unsubscribe();
    this.wsSub = null;
  }

  private subscribeToBlotter(uuid: string) {
    this.unsubscribe();
    this.setState({
      uuid,
      rows: [],
      nItems: null,
      runningState: RunningState.Running
    });

    const resubscribe = (iteration: number) => {
      if (iteration > 5) {
        console.error(
          `Failed to subscribe to ${NAME} Blotter - max retries exceeded`
        );
        return;
      }
      this.wsSub?.unsubscribe();
      this.wsSub = this.websocketService
        .getListener({ uuid, take: Infinity })
        .pipe(
          map((row: WSRow) => {
            return {
              ...row.payload,
              uuid: row.uuid
            } as Row;
          })
        )
        .subscribe({
          next: r => {
            if (r.error) {
              this.handleError(r, true);
            } else if (r.warning) {
              this.handleError(r, false);
            } else if (r.info) {
              this.handleInfo(r);
            } else if (r.item) {
              this.handleData(r);
            }
          },
          complete: async () => {
            console.log(`${NAME} Blotter subscription completed`);
            await sleep(500);
            resubscribe(iteration + 1);
          },
          error: async err => {
            console.error(err);
            await sleep(500);
            resubscribe(iteration + 1);
          }
        });
    };

    resubscribe(0);
  }

  private handleError(row: Row, abort: boolean) {
    if (abort || (row.warning && /no.*events/i.test(row.message))) {
      this.unsubscribe();
      this.setState(DefaultBlotterState);
    }
    this.alertsService.addAlert({
      type: row.error ? 'error' : 'warning',
      message: row.message || `There was a problem running the ${NAME} report`,
      config: {
        closeButton: true,
        tapToDismiss: true,
        timeOut: 3000
      }
    });
    this.ping();
  }

  private handleInfo(row: Row) {
    this.setState({ info: row.message });
  }

  private handleData(row: Row) {
    const curr = this.state.rows;
    const rows = [...curr, row.item];
    let runningState = this.state.runningState;
    let info = this.state.info;
    if (rows.length === row.nItems) {
      this.unsubscribe();
      runningState = RunningState.Idle;
      info = 'Report loaded';
      this.ping();
    }
    this.setState({ rows, nItems: row.nItems, info, runningState });
  }

  @Throttle(1000)
  private ping(): void {
    this.userStorageService.ping();
  }
}
